import defer from "lodash/defer"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import find from "lodash/find"
import { useState, useEffect } from "react"
import { Configure, Index, useSearchBox } from "react-instantsearch"
import { makeStyles } from "tss-react/mui"
import { withLocalization } from "hoc-lib/localization"
import { getWindow } from "utils-lib/getWindow"
import { useAutocomplete } from "../utils/useAutocomplete"
import Suggestions from "../components/Suggestions/Suggestions"
import Results from "../components/Results/Results"
import { Button } from "../../Base"
import { pushQueryUpdateData } from "./analytics/analytics"

const ssrWindow = getWindow()

export const strings = {
  en_CA: {
    RESULTS: `Results`,
  },
  fr_CA: {
    RESULTS: `Résultats`,
  },
}

const useStyles = makeStyles()((theme) => ({
  inputText: {
    boxShadow: `0 2px 6px 0 rgba(175,181,186,.3), 0 -1px 6px 0 rgba(175,181,186,.3)`,
  },
  svg: {
    "& > svg": {
      height: "2rem",
      width: "2rem",
    },
  },
  loadMore: {
    border: `1px solid #D2E7CE !important`,
    borderRadius: 5,
    "&:focus": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
      outline: `2px solid  ${theme.palette.primary.main}`,
    },
  },
}))

export const PageSearch = ({
  indices,
  currentRefinement,
  error = {},
  numHighlightedResults,
  suggestionIndex,
  resultsIndex,
  errorMessageTitle,
  errorMessageDescription,
  failureTitle,
  failureDescription,
  hits,
  loadMoreNumber,
  numCharacters,
  numResults,
  search,
  selectedIndex,
  strings,
  suggestedText,
  resultsText,
  loadMoreText,
  searching,
  filterType,
}) => {
  const { classes } = useStyles()

  const [numHits, setNumHits] = useState(numResults)

  const wantedIndex =
    !selectedIndex || selectedIndex === `all`
      ? resultsIndex
      : `${resultsIndex}-results`

  const allSearchResults = get(
    find(indices, ({ indexId }) => wantedIndex === indexId),
    `results`,
  )

  useEffect(() => {
    if (numHits !== numResults) {
      setNumHits(numResults)
    }
  }, [currentRefinement])

  let suggestions = []

  if (
    !selectedIndex ||
    selectedIndex === `all` ||
    (selectedIndex && selectedIndex.indexOf("support") === -1)
  ) {
    suggestions = find(hits, (item) => item.index === resultsIndex)

    if (suggestions) {
      suggestions = suggestions.hits?.slice(0, numHighlightedResults)
    }
  }

  const totalResults = allSearchResults
    ? get(allSearchResults, `nbHits`, 0) ||
      get(allSearchResults[resultsIndex], `nbHits`, 0)
    : 0

  useEffect(() => {
    if (!searching && totalResults) {
      pushQueryUpdateData(search, totalResults)
    }
  }, [totalResults])

  function renderSuggested() {
    if (selectedIndex && selectedIndex.indexOf(`support`) !== -1) {
      return null
    }

    return (
      <Index
        indexName={suggestionIndex}
        indexId={`${suggestionIndex}-suggested`}
      >
        <Configure
          analyticsTags={["site-search-suggested"]}
          hitsPerPage={numHighlightedResults}
          filters={`tags:'${filterType}'`}
          clickAnalytics
        />
        <Suggestions
          className="SiteSearch__result-results-page"
          indexName={`${suggestionIndex}-suggested`}
          hideDescription={false}
          isHeader={false}
          smallDescription={false}
        />
      </Index>
    )
  }

  useEffect(() => {
    const focusElement = () => {
      const elementToFocus = ssrWindow.document.querySelector(
        `[data-index="${numHits}"]`,
      )
      if (elementToFocus) {
        elementToFocus.focus()
      } else {
        requestAnimationFrame(focusElement)
      }
    }

    focusElement()
  }, [numHits])

  function renderResults(hideDescription = false) {
    const suggestionsLength = suggestions ? suggestions.length : 0

    return (
      <Index indexName={resultsIndex} indexId={`${resultsIndex}-results`}>
        <Configure
          analyticsTags={["site-search-results"]}
          hitsPerPage={numHits - suggestionsLength}
          clickAnalytics
          filters={
            selectedIndex && selectedIndex !== `all`
              ? `searchType:${selectedIndex.toLowerCase()}`
              : ``
          }
        />
        <Results
          className="SiteSearch__result-results-page"
          hideDescription={hideDescription}
          isHeader={false}
          smallDescription={false}
          indexName={`${resultsIndex}-results`}
          numberToRender={numHits - suggestionsLength}
        />
      </Index>
    )
  }

  return (
    <div className="maax-font-weight-regular">
      <div>
        {!searching && (
          <>
            <div
              className="d-none d-sm-block px-3 wm-text-gray-dark"
              aria-live="assertive"
              role="alert"
              aria-atomic="true"
            >
              {search.length < numCharacters
                ? suggestedText
                : `${totalResults} ${resultsText || strings.RESULTS}`}
            </div>
            <div
              className="d-sm-none font-xs py-1 px-3"
              style={{
                background: `#f4f4f4`,
              }}
            >
              <div className="px-3">
                {search.length < numCharacters
                  ? suggestedText
                  : `${totalResults} ${resultsText || strings.RESULTS}`}
              </div>
            </div>
          </>
        )}
        {totalResults === 0 && isEmpty(suggestions) && !searching && (
          <div className="mt-4 mx-3">
            <div className="mx-3 wm-grey-light text-center">
              <div className="maax-font-weight-bold">
                {error && error.name ? failureTitle : errorMessageTitle}
              </div>
              <div className="font-sm">
                {error && error.name
                  ? failureDescription
                  : errorMessageDescription}
              </div>
            </div>
          </div>
        )}
        <div>
          {renderSuggested()}
          {renderResults(search.length < numCharacters)}

          {numHits < totalResults && !!totalResults && (
            <div className="d-flex justify-content-center mt-4">
              <Button
                overrideClass={`p-0 wm-link py-2 px-4 font-sm mb-2 ${classes.loadMore}`}
                onClick={() => {
                  setNumHits(numHits + loadMoreNumber)

                  const currentScroll = ssrWindow.scrollY

                  defer(() => {
                    ssrWindow.scrollTo({
                      top: currentScroll,
                    })
                  })
                }}
              >
                {loadMoreText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// PageSearch.propTypes = {
//   indices: PropTypes.array.isRequired,
//   currentRefinement: PropTypes.string.isRequired,
//   error: PropTypes.object,
//   numHighlightedResults: PropTypes.number.isRequired,
//   suggestionIndex: PropTypes.string.isRequired,
//   resultsIndex: PropTypes.string.isRequired,
//   errorMessageTitle: PropTypes.string.isRequired,
//   errorMessageDescription: PropTypes.string.isRequired,
//   failureTitle: PropTypes.string.isRequired,
//   failureDescription: PropTypes.string.isRequired,
//   hits: PropTypes.array.isRequired,
//   loadMoreNumber: PropTypes.number.isRequired,
//   numCharacters: PropTypes.number.isRequired,
//   numResults: PropTypes.number.isRequired,
//   search: PropTypes.string.isRequired,
//   selectedIndex: PropTypes.string.isRequired,
//   strings: PropTypes.object.isRequired,
//   suggestedText: PropTypes.string.isRequired,
//   resultsText: PropTypes.string.isRequired,
//   loadMoreText: PropTypes.string.isRequired,
//   searching: PropTypes.bool.isRequired,
//   filterType: PropTypes.string.isRequired,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// PageSearch.defaultProps = {
//   error: {},
// }

const PageSearchWithContainer = (props) => {
  const stateResults = useSearchBox(props)
  const autoCompleteResults = useAutocomplete({ ...props, ...stateResults })

  return <PageSearch {...props} {...stateResults} {...autoCompleteResults} />
}

PageSearch.displayName = "PageSearch"
PageSearchWithContainer.displayName = "CustomAutoComplete"

export default withLocalization(strings)(PageSearchWithContainer)
